import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, XCircle, Ban, CheckCircle2, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealtorManagement = ({ approvedRealtors, fetchData }) => {
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, realtor: null });
  const [confirmInput, setConfirmInput] = useState('');
  const [reason, setReason] = useState('');
  const [selectedRealtors, setSelectedRealtors] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAction = async () => {
    const { action, realtor } = actionDialog;
    
    try {
      if (action === 'suspend') {
        await axios.post(
          `${API}/admin/realtors/${realtor.id}/suspend`,
          {},
          { 
            params: { reason: reason || 'Suspended by admin' },
            withCredentials: true 
          }
        );
        toast.success(`${realtor.name} suspended successfully`);
      } else if (action === 'reactivate') {
        await axios.post(
          `${API}/admin/realtors/${realtor.id}/reactivate`,
          {},
          { withCredentials: true }
        );
        toast.success(`${realtor.name} reactivated successfully`);
      } else if (action === 'delete') {
        if (confirmInput !== realtor.email) {
          toast.error('Email confirmation does not match');
          return;
        }
        await axios.delete(
          `${API}/admin/realtors/${realtor.id}`,
          { withCredentials: true }
        );
        toast.success(`${realtor.name} deleted permanently`);
      }
      
      setActionDialog({ open: false, action: null, realtor: null });
      setConfirmInput('');
      setReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} realtor`);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRealtors(approvedRealtors.map(r => r.id));
    } else {
      setSelectedRealtors([]);
    }
  };

  const handleSelectRealtor = (realtorId) => {
    if (selectedRealtors.includes(realtorId)) {
      setSelectedRealtors(selectedRealtors.filter(id => id !== realtorId));
    } else {
      setSelectedRealtors([...selectedRealtors, realtorId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRealtors.length === 0) return;
    
    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/realtors/bulk-delete`,
        { ids: selectedRealtors },
        { withCredentials: true }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} realtor(s)`);
      setSelectedRealtors([]);
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete realtors');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold">Approved Realtors ({approvedRealtors.length})</h3>
            {selectedRealtors.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedRealtors.length})
              </Button>
            )}
          </div>
        </div>
        {approvedRealtors.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No approved realtors yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRealtors.length === approvedRealtors.length && approvedRealtors.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Areas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {approvedRealtors.map(realtor => (
                  <tr key={realtor.id} className={selectedRealtors.includes(realtor.id) ? 'bg-orange-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRealtors.includes(realtor.id)}
                        onChange={() => handleSelectRealtor(realtor.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{realtor.name}</p>
                        <p className="text-xs text-gray-500">{realtor.brokerage}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>
                        <p>{realtor.email}</p>
                        <p className="text-xs">{realtor.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {realtor.areas_served.slice(0, 2).join(', ')}
                      {realtor.areas_served.length > 2 && ` +${realtor.areas_served.length - 2}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {realtor.leads_claimed_this_month} / {realtor.monthly_lead_limit}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {realtor.average_rating > 0 ? `${realtor.average_rating.toFixed(1)} ⭐` : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        realtor.status === 'active' ? 'bg-green-100 text-green-800' : 
                        realtor.status === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {realtor.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {realtor.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => setActionDialog({ open: true, action: 'suspend', realtor })}
                          >
                            <Ban className="w-4 h-4 mr-1" />
                            Suspend
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => setActionDialog({ open: true, action: 'reactivate', realtor })}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Reinstate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => setActionDialog({ open: true, action: 'delete', realtor })}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => {
        if (!open) {
          setActionDialog({ open: false, action: null, realtor: null });
          setConfirmInput('');
          setReason('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center ${
              actionDialog.action === 'delete' ? 'text-red-600' : 
              actionDialog.action === 'suspend' ? 'text-orange-600' :
              'text-green-600'
            }`}>
              <AlertTriangle className="w-5 h-5 mr-2" />
              {actionDialog.action === 'suspend' && 'Suspend Realtor'}
              {actionDialog.action === 'reactivate' && 'Reinstate Realtor'}
              {actionDialog.action === 'delete' && 'Delete Realtor - Permanent'}
            </DialogTitle>
          </DialogHeader>
          
          {actionDialog.realtor && (
            <div className="space-y-4">
              <p className="text-gray-700">
                {actionDialog.action === 'suspend' && `You are about to suspend ${actionDialog.realtor.name}. They will no longer receive leads.`}
                {actionDialog.action === 'reactivate' && `You are about to reinstate ${actionDialog.realtor.name}. They will be able to receive leads again.`}
                {actionDialog.action === 'delete' && `You are about to permanently delete ${actionDialog.realtor.name}. This action cannot be undone.`}
              </p>
              
              {actionDialog.action === 'suspend' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for suspension (optional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    rows={3}
                    placeholder="Enter reason for suspension..."
                  />
                </div>
              )}
              
              {actionDialog.action === 'delete' && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    Type the realtor&apos;s email to confirm:
                  </p>
                  <p className="text-sm text-red-600 mb-2 font-mono">{actionDialog.realtor.email}</p>
                  <input
                    type="text"
                    value={confirmInput}
                    onChange={(e) => setConfirmInput(e.target.value)}
                    className="w-full border border-red-300 rounded-md p-2 text-sm"
                    placeholder="Enter email to confirm"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog({ open: false, action: null, realtor: null });
                setConfirmInput('');
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              className={
                actionDialog.action === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                actionDialog.action === 'suspend' ? 'bg-orange-600 hover:bg-orange-700' :
                'bg-green-600 hover:bg-green-700'
              }
            >
              {actionDialog.action === 'suspend' && 'Suspend'}
              {actionDialog.action === 'reactivate' && 'Reinstate'}
              {actionDialog.action === 'delete' && 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Delete {selectedRealtors.length} Realtor(s)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                You are about to permanently delete <strong>{selectedRealtors.length}</strong> realtor(s).
                This action cannot be undone.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : `Delete ${selectedRealtors.length} Realtor(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RealtorManagement;
